import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

export function usePermissionSetManagement() {
  const store = useStore();
  const { t } = useI18n();
  const { accountId } = useAccount();
  const userId = computed(() => store.getters.getCurrentUserID);
  const management = computed(() => store.state.permissionSets.management);
  const allowed = computed(
    () =>
      store.getters.getCurrentRole === 'administrator' &&
      store.getters['accounts/isFeatureEnabledonAccount'](
        accountId.value,
        FEATURE_FLAGS.CUSTOM_ROLES_V2
      )
  );
  const editor = ref(null);
  const deleting = ref(null);
  const error = ref('');
  const notice = ref('');
  const busy = computed(
    () => management.value.loading || management.value.pendingWrites > 0
  );
  let generation = 0;
  const run = async (action, payload, success) => {
    const captured = generation;
    if (!allowed.value) return;
    error.value = '';
    notice.value = '';
    try {
      const result = await store.dispatch(
        `permissionSets/management/${action}`,
        payload
      );
      if (captured !== generation || !allowed.value || result === null) return;
      success?.();
    } catch (failure) {
      if (captured !== generation || !allowed.value) return;
      if (failure.permissionSetMutationCompleted) success?.();
      const detail = parseAPIErrorResponse(failure);
      error.value =
        typeof detail === 'string' ? detail : t('PERMISSION_SETS.FAILED');
    }
  };
  const reload = () => run('load', accountId.value);
  const save = attributes =>
    run('save', { id: editor.value.id, attributes }, () => {
      editor.value = null;
      notice.value = t('PERMISSION_SETS.SAVED');
    });
  const remove = () =>
    run('remove', deleting.value.id, () => {
      deleting.value = null;
      notice.value = t('PERMISSION_SETS.DELETED');
    });
  watch(
    [accountId, allowed, userId],
    () => {
      generation += 1;
      editor.value = null;
      deleting.value = null;
      error.value = '';
      notice.value = '';
      store.dispatch('permissionSets/management/reset');
      if (allowed.value) reload();
    },
    { immediate: true }
  );
  onUnmounted(() => {
    generation += 1;
  });
  return {
    management,
    allowed,
    editor,
    deleting,
    error,
    notice,
    busy,
    reload,
    save,
    remove,
  };
}
